export const WEATHER_LAYER_TYPES = ["wind10m", "precipitation", "lightning", "radar"] as const

export type WeatherLayerType = (typeof WEATHER_LAYER_TYPES)[number]

export const isWeatherLayerType = (t: string): t is WeatherLayerType =>
    (WEATHER_LAYER_TYPES as readonly string[]).includes(t)

export const WEATHER_TILE_PREFIX = "public/weather-tiles"

export const WEATHER_TILE_TAG = { key: "weatherTile", value: "true" } as const
export const WEATHER_TILE_TAGGING = `${WEATHER_TILE_TAG.key}=${WEATHER_TILE_TAG.value}`
export const OBSERVED_TILE_VERSION = "observed"

const canonicalIsoTime = (s: string) => {
    const ms = Date.parse(s)
    return Number.isNaN(ms) ? s : new Date(ms).toISOString()
}

export const sanitizeTileTime = (s: string) => canonicalIsoTime(s).replace(/[:]/g, "-")

export const desanitizeTileTime = (s: string) => {
    const t = s.indexOf("T")
    return t === -1 ? s : s.slice(0, t + 1) + s.slice(t + 1).replace(/-/g, ":")
}

export type WeatherTileAddress = {
    type: WeatherLayerType
    version: string
    time: string
    z: number
    x: number
    y: number
}

export const weatherTileKey = ({ type, version, time, z, x, y }: WeatherTileAddress) =>
    `${WEATHER_TILE_PREFIX}/${type}/${version}/${sanitizeTileTime(time)}/${z}/${x}/${y}`

export const weatherTileRoute = (address: WeatherTileAddress) => `/${weatherTileKey(address)}`

export const TILE_SIZE = 256
export const MIN_TILE_ZOOM = 0
export const MAX_TILE_ZOOM = 7

export const tileLon = (x: number, z: number) => (x / 2 ** z) * 360 - 180
export const tileLat = (y: number, z: number) => 90 - (y / 2 ** z) * 180

export const tileBBox = (x: number, y: number, z: number) => ({
    west: tileLon(x, z),
    east: tileLon(x + 1, z),
    north: tileLat(y, z),
    south: tileLat(y + 1, z),
})

export type TileSampleMode = "bilinear" | "nearest"

export type TileChannelRange = { min: number; max: number; sample: TileSampleMode }

export const WEATHER_TILE_CHANNELS = {
    radar: { reflectivityDbz: { min: -30, max: 66, sample: "bilinear" } },
    wind10m: {
        u: { min: -25, max: 25, sample: "bilinear" },
        v: { min: -25, max: 25, sample: "bilinear" },
    },
    precipitation: {
        mmPerHour: { min: 0, max: 51, sample: "bilinear" },
        precipType: { min: 0, max: 255, sample: "nearest" },
    },
    lightning: { flashDensityLPerKm2: { min: 0, max: 1, sample: "bilinear" } },
} as const satisfies Record<WeatherLayerType, Record<string, TileChannelRange>>

export const byteEncodeParams = ({ min, max }: { min: number; max: number }) => {
    const scale = 255 / (max - min)
    return { scale, offset: -min * scale }
}

export const byteDecode = ({ min, max }: { min: number; max: number }, byte: number) =>
    min + (byte / 255) * (max - min)

export const WEATHER_ORIGIN_TOKEN_HEADER = "x-nova-weather-origin"

export const weatherManifestKey = (type: WeatherLayerType) =>
    `${WEATHER_TILE_PREFIX}/${type}/manifest.json`

export type WeatherManifestFrame = { time: string; version: string }

export type WeatherTileManifest = {
    /** Newest forecast run seen. Display and fallback only; per-frame versions live on `frames`. */
    forecastRun?: string
    frames?: WeatherManifestFrame[]
    updatedAt: string
}

export const WEATHER_COVERAGE_DAYS_BACK = 3
export const WEATHER_COVERAGE_DAYS_FORWARD = 3
export const WEATHER_RADAR_HOURS_FORWARD = 18

export type LightningStrikeRegion = {
    slug: string
    label: string
    bbox: { north: number; south: number; east: number; west: number }
}

export const LIGHTNING_STRIKE_REGIONS: readonly LightningStrikeRegion[] = [
    {
        slug: "western-canada",
        label: "Western Canada",
        bbox: { north: 64.3, south: 49.0, west: -130.0, east: -97.2 },
    },
    {
        slug: "eastern-canada",
        label: "Eastern Canada",
        bbox: { north: 58.0, south: 43.3, west: -97.2, east: -52.0 },
    },
    {
        slug: "western-us",
        label: "Western US",
        bbox: { north: 49.0, south: 32.5, west: -125.5, east: -97.2 },
    },
    {
        slug: "southeast-us",
        label: "Southeast US",
        bbox: { north: 43.3, south: 24.4, west: -97.2, east: -67.0 },
    },
]

export const FLASH_COVERAGE_REGIONS: readonly {
    north: number
    south: number
    east: number
    west: number
}[] = [
    // Canada + contiguous United States
    { north: 84.0, south: 24.3, west: -141.1, east: -52.2 },
    // Alaska (including Aleutian Islands to the antimeridian)
    { north: 72.0, south: 51.0, west: -180.0, east: -129.9 },
]

export type StrikePolarity = "positive" | "negative"

export type GroundStrikeRecord = {
    lat: number
    lon: number
    timeMs: number
    type: "CG" | "IC"
    polarity: StrikePolarity
    peakAmpKa: number
}

export type FlashRecord = {
    lat: number
    lon: number
    timeMs: number
    type: "flash"
    radiantEnergy: number
}

export type StrikeRecord = GroundStrikeRecord | FlashRecord

export type DetailedStrikeArea = {
    startMs: number
    endMs: number
    lat: number
    lon: number
    radiusKm: number
}

export type WeatherVectorDataType = "lightningStrikes"

export type WeatherDataResponse = {
    meta: {
        requestedAt: string
        source: string
        type: WeatherVectorDataType
        partialCoverage: boolean
    }
    strikes: StrikeRecord[]
    detailedStrikeAreas: DetailedStrikeArea[]
}
